import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtErr, fmtDate, fmtDT, fmtQty, todayISO } from "../api";
import { Plus, Pencil, PackagePlus, PackageMinus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const TABS = [
  { id: "stock", label: "Current Stock" },
  { id: "addition", label: "Stock Addition" },
  { id: "deductions", label: "Stock Deductions" },
  { id: "reconciliation", label: "Reconciliation" },
  { id: "movements", label: "Movements" },
];

function ProductDialog({ open, onClose, product, meta, onSaved }) {
  const empty = { name: "", code: "", category: "", product_type: "Raw Material", uom: "Meter", opening_stock: "", min_stock: 0, production_rate: 0, alternate_uom: "", conversion_factor: "", qty_step: "", active: true, description: "" };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setF(product ? { ...empty, ...product } : empty);
  }, [open, product]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...f, opening_stock: parseFloat(f.opening_stock) || 0, min_stock: parseFloat(f.min_stock) || 0, production_rate: parseFloat(f.production_rate) || 0, conversion_factor: parseFloat(f.conversion_factor) || 0, qty_step: parseFloat(f.qty_step) || 0 };
      if (product) await api.put(`/products/${product.id}`, body);
      else await api.post("/products", body);
      toast.success(product ? "Product updated" : "Product created");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-auto" data-testid="product-dialog">
        <DialogHeader><DialogTitle className="font-heading text-[#002B5D]">{product ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 sm:col-span-1">
              <label className="sk-label">Product Name *</label>
              <input data-testid="product-name-input" className="sk-input" required value={f.name} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="sk-label">Code / SKU *</label>
              <input data-testid="product-code-input" className="sk-input font-mono" required value={f.code} onChange={(e) => set("code", e.target.value)} />
            </div>
            <div>
              <label className="sk-label">Category</label>
              <input data-testid="product-category-input" className="sk-input" value={f.category} onChange={(e) => set("category", e.target.value)} />
            </div>
            <div>
              <label className="sk-label">Type</label>
              <select data-testid="product-type-select" className="sk-input" value={f.product_type} onChange={(e) => set("product_type", e.target.value)}>
                {meta.product_types.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="sk-label">Unit of Measurement *</label>
              <input data-testid="product-uom-input" className="sk-input" required list="uom-list" value={f.uom} onChange={(e) => set("uom", e.target.value)} />
              <datalist id="uom-list">{meta.uoms.map((u) => <option key={u} value={u} />)}</datalist>
            </div>
            <div>
              <label className="sk-label">Minimum Stock Level</label>
              <input data-testid="product-min-stock-input" type="number" step="any" className="sk-input" value={f.min_stock} onChange={(e) => set("min_stock", e.target.value)} />
            </div>
            {!product && (
              <div>
                <label className="sk-label">Opening Stock</label>
                <input data-testid="product-opening-stock-input" type="number" step="any" className="sk-input" value={f.opening_stock} onChange={(e) => set("opening_stock", e.target.value)} />
              </div>
            )}
            <div>
              <label className="sk-label">Production Rate ({f.uom}/hour)</label>
              <input data-testid="product-rate-input" type="number" step="any" className="sk-input" value={f.production_rate} onChange={(e) => set("production_rate", e.target.value)} />
            </div>
            <div>
              <label className="sk-label">Alternate UOM</label>
              <input data-testid="product-alt-uom-input" className="sk-input" list="uom-list" placeholder="e.g. Box, BAG" value={f.alternate_uom} onChange={(e) => set("alternate_uom", e.target.value)} />
            </div>
            <div>
              <label className="sk-label">Conversion (1 {f.alternate_uom || "ALT"} = ? {f.uom})</label>
              <input data-testid="product-conversion-input" type="number" step="any" className="sk-input" placeholder="e.g. 25" value={f.conversion_factor} onChange={(e) => set("conversion_factor", e.target.value)} />
            </div>
            <div>
              <label className="sk-label">Quantity Step</label>
              <input data-testid="product-qty-step-input" type="number" step="any" className="sk-input" placeholder={f.product_type === "Finished Good" ? "1000" : "1"} value={f.qty_step} onChange={(e) => set("qty_step", e.target.value)} />
            </div>
            <div className="col-span-2">
              <label className="sk-label">Description</label>
              <textarea data-testid="product-description-input" className="sk-input h-20 py-2" value={f.description} onChange={(e) => set("description", e.target.value)} />
            </div>
            <label className="col-span-2 flex items-center gap-2 text-sm">
              <input data-testid="product-active-checkbox" type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} className="w-4 h-4" />
              Active
            </label>
          </div>
          <button data-testid="product-save-button" disabled={busy} className="w-full h-11 rounded-md font-heading font-bold text-white disabled:opacity-60" style={{ backgroundColor: "#002B5D" }}>
            {busy ? "Saving..." : "Save Product"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AdjustDialog({ open, onClose, product, onSaved }) {
  const [f, setF] = useState({ qty: "", type: "Stock Adjustment", reason: "", remarks: "", uom: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ qty: "", type: "Stock Adjustment", reason: "", remarks: "", uom: "" }); }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/inventory/adjustments", { product_id: product.id, qty: parseFloat(f.qty), type: f.type, reason: f.reason, remarks: f.remarks, uom: f.uom || product.uom });
      toast.success("Stock updated");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="adjust-dialog">
        <DialogHeader><DialogTitle className="font-heading text-[#002B5D]">Adjust Stock — {product?.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Current: <span className="font-mono font-semibold">{fmtQty(product?.current_stock)} {product?.uom}</span>. Use negative qty to reduce (e.g. -2).</p>
        <form onSubmit={submit} noValidate className="space-y-4 mt-2">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="sk-label">Quantity *</label>
              <input data-testid="adjust-qty-input" type="number" step={product?.qty_step > 1 ? product.qty_step : "any"} required className="sk-input font-mono" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} />
            </div>
            <div>
              <label className="sk-label">UOM</label>
              <select data-testid="adjust-uom-select" className="sk-input" value={f.uom || product?.uom} onChange={(e) => setF({ ...f, uom: e.target.value })}>
                <option value={product?.uom}>{product?.uom}</option>
                {product?.alternate_uom && <option value={product.alternate_uom}>{product.alternate_uom} (1 = {product.conversion_factor} {product.uom})</option>}
              </select>
            </div>
            <div>
              <label className="sk-label">Type</label>
              <select data-testid="adjust-type-select" className="sk-input" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
                <option>Stock Adjustment</option>
                <option>Stock Addition</option>
              </select>
            </div>
          </div>
          <div>
            <label className="sk-label">Reason *</label>
            <input data-testid="adjust-reason-input" required className="sk-input" placeholder="e.g. Physical count correction" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />
          </div>
          <div>
            <label className="sk-label">Remarks</label>
            <input data-testid="adjust-remarks-input" className="sk-input" value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} />
          </div>
          <button data-testid="adjust-save-button" disabled={busy} className="w-full h-11 rounded-md font-heading font-bold text-white disabled:opacity-60" style={{ backgroundColor: "#002B5D" }}>
            {busy ? "Saving..." : "Record Adjustment"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeductionDialog({ open, onClose, onSaved }) {
  const [lots, setLots] = useState([]);
  const [f, setF] = useState({ lot_id: "", qty: "", date: todayISO(), customer_name: "", reference_no: "", remarks: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setF({ lot_id: "", qty: "", date: todayISO(), customer_name: "", reference_no: "", remarks: "" });
      api.get("/lots").then((r) => setLots(r.data.filter((l) => l.current_qty > 0))).catch(() => {});
    }
  }, [open]);

  const lot = lots.find((l) => l.id === f.lot_id);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/deductions", { ...f, qty: parseFloat(f.qty) });
      toast.success("Stock deducted");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="deduction-dialog">
        <DialogHeader><DialogTitle className="font-heading text-[#002B5D]">Manual Stock Deduction</DialogTitle></DialogHeader>
        <form onSubmit={submit} noValidate className="space-y-4">
          <div>
            <label className="sk-label">Lot *</label>
            <select data-testid="deduction-lot-select" required className="sk-input" value={f.lot_id} onChange={(e) => setF({ ...f, lot_id: e.target.value })}>
              <option value="">Select lot...</option>
              {lots.map((l) => (
                <option key={l.id} value={l.id}>{l.lot_number} — {l.product_name} ({fmtQty(l.current_qty)} {l.uom} available)</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="sk-label">Quantity * {lot && <span className="normal-case text-slate-400">(max {fmtQty(lot.current_qty)})</span>}</label>
              <input data-testid="deduction-qty-input" type="number" step="any" min="0.001" max={lot?.current_qty} required className="sk-input font-mono" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} />
            </div>
            <div>
              <label className="sk-label">Date</label>
              <input data-testid="deduction-date-input" type="date" className="sk-input" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="sk-label">Customer Name</label>
            <input data-testid="deduction-customer-input" className="sk-input" value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} />
          </div>
          <div>
            <label className="sk-label">Invoice / Reference No.</label>
            <input data-testid="deduction-reference-input" className="sk-input font-mono" value={f.reference_no} onChange={(e) => setF({ ...f, reference_no: e.target.value })} />
          </div>
          <div>
            <label className="sk-label">Remarks</label>
            <input data-testid="deduction-remarks-input" className="sk-input" value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} />
          </div>
          <button data-testid="deduction-save-button" disabled={busy} className="w-full h-11 rounded-md font-heading font-bold text-white disabled:opacity-60" style={{ backgroundColor: "#EB1C27" }}>
            {busy ? "Saving..." : "Deduct Stock"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AdditionTab() {
  const [products, setProducts] = useState([]);
  const [history, setHistory] = useState([]);
  const [f, setF] = useState({ product_id: "", qty: "", uom: "", date: todayISO(), reference: "", supplier: "", remarks: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/products", { params: { active: "true" } }).then((r) => setProducts(r.data)).catch(() => {});
    api.get("/inventory/movements", { params: { movement_type: "Stock Addition", limit: 20 } }).then((r) => setHistory(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const product = products.find((p) => p.id === f.product_id);
  const uom = f.uom || product?.uom || "";
  const primaryQty = product && f.uom && f.uom === product.alternate_uom && product.conversion_factor
    ? (parseFloat(f.qty) || 0) * product.conversion_factor : parseFloat(f.qty) || 0;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/inventory/additions", { ...f, qty: parseFloat(f.qty), uom });
      toast.success(`Stock added — new stock: ${r.data.current_stock} ${product?.uom}`);
      setF({ product_id: "", qty: "", uom: "", date: todayISO(), reference: "", supplier: "", remarks: "" });
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="addition-tab">
      <form onSubmit={submit} noValidate className="sk-card p-6 space-y-4 self-start">
        <h3 className="font-heading font-bold text-[#002B5D]">Add Stock to Existing Product</h3>
        <div>
          <label className="sk-label">Product *</label>
          <select data-testid="addition-product-select" required className="sk-input" value={f.product_id} onChange={(e) => setF({ ...f, product_id: e.target.value, uom: "" })}>
            <option value="">Select product...</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.code}) — current {fmtQty(p.current_stock)} {p.uom}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="sk-label">Quantity *</label>
            <input data-testid="addition-qty-input" type="number" step={product?.qty_step > 1 ? product.qty_step : "any"} min="0" required className="sk-input font-mono" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} />
          </div>
          <div>
            <label className="sk-label">UOM</label>
            <select data-testid="addition-uom-select" className="sk-input" value={uom} onChange={(e) => setF({ ...f, uom: e.target.value })} disabled={!product}>
              {product && <option value={product.uom}>{product.uom}</option>}
              {product?.alternate_uom && <option value={product.alternate_uom}>{product.alternate_uom}</option>}
            </select>
          </div>
        </div>
        {product && f.uom === product.alternate_uom && f.qty && (
          <div className="text-xs bg-sky-50 border border-sky-200 text-sky-800 rounded-md px-3 py-2" data-testid="addition-conversion-note">
            = <span className="font-mono font-semibold">{fmtQty(primaryQty)} {product.uom}</span> (1 {product.alternate_uom} = {product.conversion_factor} {product.uom})
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="sk-label">Date</label>
            <input data-testid="addition-date-input" type="date" className="sk-input" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
          </div>
          <div>
            <label className="sk-label">Reference</label>
            <input data-testid="addition-reference-input" className="sk-input" placeholder="e.g. GRN-001" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} />
          </div>
        </div>
        <div>
          <label className="sk-label">Supplier (optional)</label>
          <input data-testid="addition-supplier-input" className="sk-input" value={f.supplier} onChange={(e) => setF({ ...f, supplier: e.target.value })} />
        </div>
        <div>
          <label className="sk-label">Remarks</label>
          <input data-testid="addition-remarks-input" className="sk-input" value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} />
        </div>
        <button data-testid="addition-submit" disabled={busy} className="w-full h-11 rounded-md font-heading font-bold text-white disabled:opacity-60" style={{ backgroundColor: "#002B5D" }}>
          {busy ? "Adding..." : "Add Stock"}
        </button>
      </form>
      <div className="sk-card overflow-x-auto self-start">
        <h3 className="font-heading font-bold text-[#002B5D] px-5 py-4 border-b border-border">Recent Additions</h3>
        <table className="w-full sk-table" data-testid="addition-history">
          <thead><tr><th>Date</th><th>Product</th><th className="text-right">Qty</th><th>Reference</th><th>By</th></tr></thead>
          <tbody>
            {history.length === 0 && <tr><td colSpan={5} className="text-center text-slate-400">No additions yet</td></tr>}
            {history.map((m) => (
              <tr key={m.id}>
                <td className="text-slate-500">{fmtDT(m.date)}</td>
                <td>{m.product_name}</td>
                <td className="text-right font-mono font-semibold text-green-700">+{fmtQty(m.qty)} {m.uom}</td>
                <td className="font-mono text-xs">{m.reference}</td>
                <td className="text-xs text-slate-500">{m.user}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReconciliationTab() {
  const [products, setProducts] = useState([]);
  const [history, setHistory] = useState([]);
  const [f, setF] = useState({ product_id: "", physical_qty: "", uom: "", date: todayISO(), reason: "Monthly physical reconciliation", remarks: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/products", { params: { active: "true" } }).then((r) => setProducts(r.data)).catch(() => {});
    api.get("/inventory/reconciliations").then((r) => setHistory(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const product = products.find((p) => p.id === f.product_id);
  const uom = f.uom || product?.uom || "";
  const physicalPrimary = product && f.uom && f.uom === product.alternate_uom && product.conversion_factor
    ? (parseFloat(f.physical_qty) || 0) * product.conversion_factor : parseFloat(f.physical_qty) || 0;
  const systemQty = product?.current_stock ?? 0;
  const diff = f.physical_qty !== "" && product ? physicalPrimary - systemQty : null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/inventory/reconciliations", { ...f, physical_qty: parseFloat(f.physical_qty), uom });
      toast.success("Reconciliation recorded");
      setF({ product_id: "", physical_qty: "", uom: "", date: todayISO(), reason: "Monthly physical reconciliation", remarks: "" });
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="reconciliation-tab">
      <form onSubmit={submit} noValidate className="sk-card p-6 space-y-4 self-start">
        <h3 className="font-heading font-bold text-[#002B5D]">Stock Reconciliation (Physical Count)</h3>
        <div>
          <label className="sk-label">Product *</label>
          <select data-testid="recon-product-select" required className="sk-input" value={f.product_id} onChange={(e) => setF({ ...f, product_id: e.target.value, uom: "", physical_qty: "" })}>
            <option value="">Select product...</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.code})</option>)}
          </select>
        </div>
        {product && (
          <div className="text-sm bg-slate-50 border border-border rounded-md px-4 py-3 space-y-1" data-testid="recon-summary">
            <div className="flex justify-between"><span className="text-slate-500">System Quantity</span><span className="font-mono font-semibold" data-testid="recon-system-qty">{fmtQty(systemQty)} {product.uom}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Physical Quantity</span><span className="font-mono font-semibold" data-testid="recon-physical-qty">{f.physical_qty === "" ? "-" : `${fmtQty(physicalPrimary)} ${product.uom}`}</span></div>
            <div className="flex justify-between border-t border-border pt-1"><span className="font-semibold">Difference</span>
              <span className={`font-mono font-bold ${diff < 0 ? "text-rose-600" : "text-green-700"}`} data-testid="recon-diff">{diff === null ? "-" : `${diff > 0 ? "+" : ""}${fmtQty(diff)} ${product.uom}`}</span></div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="sk-label">Physical Quantity *</label>
            <input data-testid="recon-qty-input" type="number" step={product?.qty_step > 1 ? product.qty_step : "any"} min="0" required className="sk-input font-mono" value={f.physical_qty} onChange={(e) => setF({ ...f, physical_qty: e.target.value })} />
          </div>
          <div>
            <label className="sk-label">UOM</label>
            <select data-testid="recon-uom-select" className="sk-input" value={uom} onChange={(e) => setF({ ...f, uom: e.target.value })} disabled={!product}>
              {product && <option value={product.uom}>{product.uom}</option>}
              {product?.alternate_uom && <option value={product.alternate_uom}>{product.alternate_uom}</option>}
            </select>
          </div>
          <div>
            <label className="sk-label">Date</label>
            <input data-testid="recon-date-input" type="date" className="sk-input" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
          </div>
          <div>
            <label className="sk-label">Reason</label>
            <input data-testid="recon-reason-input" className="sk-input" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />
          </div>
        </div>
        <div>
          <label className="sk-label">Remarks</label>
          <input data-testid="recon-remarks-input" className="sk-input" value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} />
        </div>
        <button data-testid="recon-submit" disabled={busy || !product} className="w-full h-11 rounded-md font-heading font-bold text-white disabled:opacity-60" style={{ backgroundColor: "#EB1C27" }}>
          {busy ? "Recording..." : "Record Reconciliation"}
        </button>
      </form>
      <div className="sk-card overflow-x-auto self-start">
        <h3 className="font-heading font-bold text-[#002B5D] px-5 py-4 border-b border-border">Reconciliation History</h3>
        <table className="w-full sk-table" data-testid="recon-history">
          <thead><tr><th>Date</th><th>Product</th><th className="text-right">System</th><th className="text-right">Physical</th><th className="text-right">Diff</th><th className="text-right">Final</th><th>By</th></tr></thead>
          <tbody>
            {history.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400">No reconciliations yet</td></tr>}
            {history.map((r) => (
              <tr key={r.id} data-testid={`recon-row-${r.id}`}>
                <td className="text-slate-500">{fmtDate(r.date)}</td>
                <td>{r.product_name}</td>
                <td className="text-right font-mono">{fmtQty(r.system_qty)}</td>
                <td className="text-right font-mono">{fmtQty(r.physical_qty)}</td>
                <td className={`text-right font-mono font-semibold ${r.difference < 0 ? "text-rose-600" : "text-green-700"}`}>{r.difference > 0 ? "+" : ""}{fmtQty(r.difference)}</td>
                <td className="text-right font-mono font-semibold">{fmtQty(r.final_qty)} {r.uom}</td>
                <td className="text-xs text-slate-500">{r.user}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Inventory() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "stock";
  const [meta, setMeta] = useState({ uoms: [], product_types: [], movement_types: [] });
  const [products, setProducts] = useState(null);
  const [movements, setMovements] = useState(null);
  const [deductions, setDeductions] = useState(null);
  const [search, setSearch] = useState(params.get("search") || "");
  const [typeFilter, setTypeFilter] = useState(params.get("type") || "");
  const [moveType, setMoveType] = useState("");
  const [productDialog, setProductDialog] = useState({ open: false, product: null });
  const [adjustDialog, setAdjustDialog] = useState({ open: false, product: null });
  const [deductOpen, setDeductOpen] = useState(false);

  const loadProducts = useCallback(() => {
    api.get("/products", { params: { search, product_type: typeFilter } }).then((r) => setProducts(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, [search, typeFilter]);
  const loadMovements = useCallback(() => {
    api.get("/inventory/movements", { params: { movement_type: moveType } }).then((r) => setMovements(r.data)).catch(() => {});
  }, [moveType]);
  const loadDeductions = useCallback(() => {
    api.get("/deductions").then((r) => setDeductions(r.data)).catch(() => {});
  }, []);

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (tab === "stock") loadProducts(); }, [tab, loadProducts]);
  useEffect(() => { if (tab === "movements") loadMovements(); }, [tab, loadMovements]);
  useEffect(() => { if (tab === "deductions") loadDeductions(); }, [tab, loadDeductions]);

  const setTab = (t) => setParams((p) => { p.set("tab", t); return p; });

  return (
    <div data-testid="inventory-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-3xl font-extrabold text-[#002B5D] tracking-tight">Inventory</h1>
        <div className="flex gap-2">
          <button data-testid="new-deduction-button" onClick={() => setDeductOpen(true)}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md font-heading font-bold text-sm text-white" style={{ backgroundColor: "#EB1C27" }}>
            <PackageMinus className="w-4 h-4" /> Stock Deduction
          </button>
          <button data-testid="add-product-button" onClick={() => setProductDialog({ open: true, product: null })}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md font-heading font-bold text-sm text-white" style={{ backgroundColor: "#002B5D" }}>
            <Plus className="w-4 h-4" /> Add Product
          </button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-border" data-testid="inventory-tabs">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`inventory-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t.id ? "border-[#EB1C27] text-[#002B5D]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "stock" && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <input data-testid="inventory-search-input" placeholder="Search name or code..." value={search} onChange={(e) => setSearch(e.target.value)} className="sk-input max-w-xs" />
            <select data-testid="inventory-type-filter" className="sk-input max-w-[200px]" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="">All types</option>
              {meta.product_types.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="sk-card overflow-x-auto">
            <table className="w-full sk-table" data-testid="inventory-table">
              <thead>
                <tr><th>Product</th><th>Code</th><th>Category</th><th>Type</th><th className="text-right">Current Qty</th><th>UOM</th><th className="text-right">Min</th><th></th></tr>
              </thead>
              <tbody>
                {!products && <tr><td colSpan={8} className="text-center text-slate-400">Loading...</td></tr>}
                {products?.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400">No products. Click Add Product.</td></tr>}
                {products?.map((p) => {
                  const low = p.min_stock > 0 && p.current_stock <= p.min_stock;
                  return (
                    <tr key={p.id} data-testid={`inventory-row-${p.code}`} className={low ? "bg-rose-50/60" : ""}>
                      <td className={`font-medium ${p.active ? "" : "text-slate-400 line-through"}`}>{p.name}</td>
                      <td className="font-mono text-[#002B5D]">{p.code}</td>
                      <td className="text-slate-500">{p.category || "-"}</td>
                      <td className="text-slate-500">{p.product_type}</td>
                      <td className={`text-right font-mono font-semibold ${low ? "text-rose-600" : ""}`} data-testid={`stock-qty-${p.code}`}>{fmtQty(p.current_stock)}</td>
                      <td>{p.uom}</td>
                      <td className="text-right font-mono text-slate-400">{fmtQty(p.min_stock)}</td>
                      <td>
                        <div className="flex gap-1 justify-end">
                          <button data-testid={`adjust-${p.code}`} title="Adjust stock" onClick={() => setAdjustDialog({ open: true, product: p })}
                            className="p-2 rounded hover:bg-slate-100 text-[#002B5D]"><PackagePlus className="w-4 h-4" /></button>
                          <button data-testid={`edit-${p.code}`} title="Edit product" onClick={() => setProductDialog({ open: true, product: p })}
                            className="p-2 rounded hover:bg-slate-100 text-slate-500"><Pencil className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "addition" && <AdditionTab />}

      {tab === "reconciliation" && <ReconciliationTab />}

      {tab === "movements" && (
        <div className="space-y-4">
          <select data-testid="movement-type-filter" className="sk-input max-w-[240px]" value={moveType} onChange={(e) => setMoveType(e.target.value)}>
            <option value="">All movement types</option>
            {meta.movement_types.map((t) => <option key={t}>{t}</option>)}
          </select>
          <div className="sk-card overflow-x-auto">
            <table className="w-full sk-table" data-testid="movements-table">
              <thead><tr><th>Date</th><th>Product</th><th>Type</th><th className="text-right">Qty</th><th>UOM</th><th>Reference</th><th>User</th><th>Remarks</th></tr></thead>
              <tbody>
                {!movements && <tr><td colSpan={8} className="text-center text-slate-400">Loading...</td></tr>}
                {movements?.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400">No movements yet</td></tr>}
                {movements?.map((m) => (
                  <tr key={m.id} data-testid={`movement-row-${m.id}`}>
                    <td className="text-slate-500">{fmtDT(m.date)}</td>
                    <td>{m.product_name} <span className="font-mono text-xs text-slate-400">{m.product_code}</span></td>
                    <td>{m.type}</td>
                    <td className={`text-right font-mono font-semibold ${m.qty < 0 ? "text-rose-600" : "text-green-700"}`}>{m.qty > 0 ? "+" : ""}{fmtQty(m.qty)}</td>
                    <td>{m.uom}</td>
                    <td className="font-mono text-xs">{m.reference}</td>
                    <td className="text-slate-500 text-xs">{m.user}</td>
                    <td className="text-slate-500 text-xs max-w-[200px] truncate">{m.remarks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "deductions" && (
        <div className="sk-card overflow-x-auto">
          <table className="w-full sk-table" data-testid="deductions-table">
            <thead><tr><th>Date</th><th>Lot</th><th>Product</th><th className="text-right">Qty</th><th>Customer</th><th>Reference</th><th>By</th></tr></thead>
            <tbody>
              {!deductions && <tr><td colSpan={7} className="text-center text-slate-400">Loading...</td></tr>}
              {deductions?.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400">No deductions yet</td></tr>}
              {deductions?.map((d) => (
                <tr key={d.id} data-testid={`deduction-row-${d.id}`}>
                  <td className="text-slate-500">{fmtDate(d.date)}</td>
                  <td className="font-mono font-semibold text-[#002B5D]">{d.lot_number}</td>
                  <td>{d.product_name}</td>
                  <td className="text-right font-mono text-rose-600 font-semibold">-{fmtQty(d.qty)} {d.uom}</td>
                  <td>{d.customer_name || "-"}</td>
                  <td className="font-mono text-xs">{d.reference_no || "-"}</td>
                  <td className="text-slate-500 text-xs">{d.created_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ProductDialog open={productDialog.open} product={productDialog.product} meta={meta} onClose={() => setProductDialog({ open: false, product: null })} onSaved={loadProducts} />
      <AdjustDialog open={adjustDialog.open} product={adjustDialog.product} onClose={() => setAdjustDialog({ open: false, product: null })} onSaved={loadProducts} />
      <DeductionDialog open={deductOpen} onClose={() => setDeductOpen(false)} onSaved={() => { loadDeductions(); loadProducts(); }} />
    </div>
  );
}
